import { Component } from "@theme/component";
import { SellingPlanUpdateEvent, ThemeEvents } from "@theme/events";
/** @typedef {import('../src/utils/analytics')} GRAnalytics */

/**
 * @typedef {object} GRProductOptionsRefs
 * @property {HTMLElement} subscriptionOption - The subscription option container
 * @property {HTMLElement} oneTimeOption - The one-time purchase option container
 * @property {HTMLSelectElement} [frequencySelect] - The frequency dropdown
 * @property {HTMLElement} [subscriptionIndicator] - The subscription radio indicator
 * @property {HTMLElement} [oneTimeIndicator] - The one-time radio indicator
 * @property {HTMLElement} [annualSavingsText] - The annual savings amount span
 */

/**
 * A custom element that manages product purchase options (subscription vs one-time).
 * Emits SellingPlanUpdateEvent when the selection changes.
 *
 * @extends Component<GRProductOptionsRefs>
 */
class GRProductOptions extends Component {
  /** @type {'subscription' | 'one-time'} */
  #selectedType = "subscription";

  #abortController = new AbortController();

  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();

    // Set initial type based on data attributes
    const subscriptionSelected =
      this.refs.subscriptionOption?.getAttribute("data-selected") === "true";
    this.#selectedType = subscriptionSelected ? "subscription" : "one-time";

    const { signal } = this.#abortController;
    this.#eventTarget = this.closest(
      ".shopify-section, dialog, product-card, added-to-cart-drawer",
    );
    this.#eventTarget?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal },
    );

    // Listen for selling plan updates from frequency modal
    this.#eventTarget?.addEventListener(
      SellingPlanUpdateEvent.eventName,
      this.#onExternalSellingPlanUpdate,
      { signal },
    );

    // Emit initial selling plan event
    this.#emitSellingPlanUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles variant update events - re-emit selling plan to ensure form is in sync
   */
  #onVariantUpdate = () => {
    // Re-emit the current selling plan selection when variant changes
    // This ensures the form always has the correct selling plan value
    this.#emitSellingPlanUpdate();
  };

  /**
   * Handles selling plan updates from external source (frequency modal)
   * @param {CustomEvent} event
   */
  #onExternalSellingPlanUpdate = (event) => {
    const { frequency, type, sourceId } = event.detail;

    if (sourceId === this.id || sourceId === "gr-product-options") return;

    if (type === "subscription" && frequency && this.refs.frequencySelect) {
      for (const option of this.refs.frequencySelect.options) {
        if (option.value === frequency) {
          this.refs.frequencySelect.value = frequency;
          break;
        }
      }

      if (this.#selectedType !== "subscription") {
        this.#selectedType = "subscription";
        this.#updateUI();
      }
    }
  };

  /**
   * Handles click on the subscription option
   * @param {MouseEvent} [event]
   * @param {boolean} [forceEmit=false] - Force emit event even if already subscription
   */
  selectSubscription(event, forceEmit = false) {
    // Don't trigger if clicking on the select dropdown
    if (
      event?.target instanceof HTMLSelectElement ||
      event?.target instanceof HTMLOptionElement
    ) {
      return;
    }

    if (this.#selectedType === "subscription" && !forceEmit) return;

    this.#selectedType = "subscription";
    this.#updateUI();
    this.#emitSellingPlanUpdate();

    if (!forceEmit) this.#trackSubscriptionSelected();
  }

  /**
   * Opens the frequency modal and ensures subscription is selected.
   * Used by the frequency link button inside the subscription option.
   *
   * @param {MouseEvent} event
   */
  openFrequencyModal(event) {
    this.selectSubscription(event, true);

    const target = event?.target instanceof Element ? event.target : null;
    const button = target?.closest("[data-frequency-modal-id]");
    const modalId =
      button instanceof HTMLElement ? button.dataset.frequencyModalId : null;

    if (!modalId) return;

    /** @type {ParentNode | null} */
    const scope =
      this.closest("added-to-cart-drawer") ||
      this.closest("dialog") ||
      this.closest(".shopify-section") ||
      this;

    /** @type {HTMLElement | null} */
    const scopedModal =
      scope && "querySelector" in scope
        ? /** @type {ParentNode} */ (scope).querySelector(
            `#${CSS.escape(modalId)}`,
          )
        : null;

    const modal = scopedModal || document.getElementById(modalId);
    if (
      modal &&
      "showDialog" in modal &&
      typeof modal.showDialog === "function"
    ) {
      modal.showDialog();
    }
  }

  /**
   * Handles click on the one-time option
   */
  selectOneTime() {
    if (this.#selectedType === "one-time") return;

    this.#selectedType = "one-time";
    this.#updateUI();
    this.#emitSellingPlanUpdate();
    this.#trackOneTimeSelected();
  }

  /**
   * Handles keydown on option elements (Space/Enter to select)
   * @param {KeyboardEvent} event
   */
  handleOptionKeydown(event) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      const target =
        event.target instanceof Element ? event.target.closest("[ref]") : null;
      const ref = target?.getAttribute("ref");
      if (ref === "subscriptionOption") {
        this.selectSubscription(event);
      } else if (ref === "oneTimeOption") {
        this.selectOneTime();
      }
    }
  }

  /**
   * Handles frequency selection change
   */
  handleFrequencyChange() {
    this.#emitSellingPlanUpdate();
    this.#updateAnnualSavings();
  }

  /**
   * Handles the subscription CTA button click.
   * Ensures subscription is selected, then submits the form.
   */
  handleSubscriptionCTA() {
    if (this.#selectedType !== "subscription") {
      this.#selectedType = "subscription";
      this.#emitSellingPlanUpdate();
    }

    this.#submitForm();
  }

  /**
   * Handles the one-time purchase CTA button click.
   * Switches to one-time and submits the form.
   * For variant buy block, routes to cart drawer instead of added-to-cart drawer.
   */
  handleOneTimeCTA() {
    this.#selectedType = "one-time";
    this.#emitSellingPlanUpdate();
    this.#trackOneTimeSelected();
    this.#submitForm();
  }

  /**
   * Updates the visual state of the options
   */
  #updateUI() {
    const isSubscription = this.#selectedType === "subscription";

    // Update subscription option
    if (this.refs.subscriptionOption) {
      this.refs.subscriptionOption.setAttribute(
        "data-selected",
        String(isSubscription),
      );
      this.#updateOptionStyles(this.refs.subscriptionOption, isSubscription);
    }

    // Update one-time option
    if (this.refs.oneTimeOption) {
      this.refs.oneTimeOption.setAttribute(
        "data-selected",
        String(!isSubscription),
      );
      this.#updateOptionStyles(this.refs.oneTimeOption, !isSubscription);
    }

    // Update indicators
    if (this.refs.subscriptionIndicator) {
      this.refs.subscriptionIndicator.classList.toggle(
        "hidden",
        !isSubscription,
      );
    }

    if (this.refs.oneTimeIndicator) {
      this.refs.oneTimeIndicator.classList.toggle("hidden", isSubscription);
    }
  }

  /**
   * Updates the styling of an option container based on selection state
   * @param {HTMLElement} element
   * @param {boolean} isSelected
   */
  #updateOptionStyles(element, isSelected) {
    if (isSelected) {
      element.classList.remove("border", "border-[#D8CFBB]", "bg-white");
      element.classList.add(
        "border-[2.5px]",
        "border-black-warm",
        "bg-[#BDB0934D]",
      );
    } else {
      element.classList.remove(
        "border-[2.5px]",
        "border-black-warm",
        "bg-[#BDB0934D]",
      );
      element.classList.add("border", "border-[#D8CFBB]", "bg-white");
    }
  }

  /**
   * Get current frequency from select dropdown
   * @returns {string | undefined}
   */
  #getCurrentFrequency() {
    if (this.refs.frequencySelect) {
      return this.refs.frequencySelect.value || undefined;
    }
    return undefined;
  }

  /**
   * Track subscription selected event
   */
  #trackSubscriptionSelected() {
    window.grAnalytics?.trackSubscriptionSelected({
      frequency: this.#getCurrentFrequency(),
      product_id: this.dataset.productId,
      product_name: this.dataset.productName,
    });
  }

  /**
   * Track one-time selected event
   */
  #trackOneTimeSelected() {
    window.grAnalytics?.trackOneTimeSelected({
      product_id: this.dataset.productId,
      product_name: this.dataset.productName,
    });
  }

  /**
   * Programmatically clicks the product-form-component submit button.
   */
  #submitForm() {
    // Prefer form inside this element (variant layout), else scope to nearest container
    const form =
      this.querySelector("product-form-component form") ||
      (this.closest("block, div, section") || document).querySelector(
        "product-form-component form",
      );
    if (!form) return;

    const submitButton = /** @type {HTMLButtonElement | null} */ (
      form.querySelector("button[name='add']")
    );

    if (submitButton && !submitButton.disabled) {
      submitButton.click();
    }
  }

  /**
   * Recalculates and updates the annual savings display based on selected frequency.
   */
  #updateAnnualSavings() {
    const el = /** @type {HTMLElement | null} */ (this.refs.annualSavingsText);
    if (!el) return;

    const savingsAmount = parseInt(this.dataset.savingsAmount || "0", 10);
    if (!savingsAmount) return;

    const frequency = this.refs.frequencySelect?.value || "";
    const weekMatch = frequency.match(/(\d+)/);
    const weeks = weekMatch ? parseInt(weekMatch[1], 10) : 0;

    if (weeks <= 0) return;

    const deliveriesPerYear = Math.floor(52 / weeks);
    const annualCents = savingsAmount * deliveriesPerYear;

    el.textContent = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(annualCents / 100);
  }

  /**
   * Emits the SellingPlanUpdateEvent with current selection
   */
  #emitSellingPlanUpdate() {
    let sellingPlanId = null;
    let frequency = undefined;

    if (this.#selectedType === "subscription" && this.refs.frequencySelect) {
      const selectedOption =
        this.refs.frequencySelect.options[
          this.refs.frequencySelect.selectedIndex
        ];
      sellingPlanId = selectedOption?.dataset.sellingPlanId || null;
      frequency = this.refs.frequencySelect.value;
    }

    this.dispatchEvent(
      new SellingPlanUpdateEvent(
        {
          sellingPlanId,
          type: this.#selectedType,
          frequency,
        },
        this.id || "gr-product-options",
      ),
    );
  }
}

if (!customElements.get("gr-product-options")) {
  customElements.define("gr-product-options", GRProductOptions);
}
